import { LitElement, html } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { ifDefined } from "lit/directives/if-defined.js";
import type { ContentPayload, LessonData, MidiNote, Study, Tempo } from "../types";
import { profileMap, techniqueMap } from "../services/content";
import { drumRows, enabledDrumRows, rowForDrumNote, type DrumRow } from "../services/drum-map";
import { StudioAudio } from "../services/studio-audio";

const velocityLevel = (velocity: number): number => velocity < 58 ? 0.35 : velocity < 102 ? 0.68 : 1;
const strengthClass = (level: number): string => level === 0 ? "" : level < 0.5 ? "hit soft-hit" : level < 0.9 ? "hit normal-hit" : "hit strong-hit";
type PlaybackMode = "studio" | "sandbox";
type StemChannel = "guitar" | "bass" | "drums";
const enabledStemChannels = (): Record<StemChannel, boolean> => ({
  guitar: true,
  bass: true,
  drums: true,
});
const defaultStemLevels = (): Record<StemChannel, number> => ({
  guitar: 0,
  bass: 0,
  drums: 0,
});
const defaultFamilyLevels = (): Record<DrumRow, number> => ({
  kick: 0,
  snare: 0,
  hiHat: 0,
  ride: 0,
  crash: 0,
  rackTom: 0,
  floorTom: 0,
});
const timestamp = (seconds: number): string => {
  const rounded = Math.round(seconds);
  return `${Math.floor(rounded / 60)}:${String(rounded % 60).padStart(2, "0")}`;
};

@customElement("study-page")
export class StudyPage extends LitElement {
  @property({ attribute: false }) content!: ContentPayload;
  @property() slug = "";
  @state() private lesson?: LessonData;
  @state() private loading = false;
  @state() private error = "";
  @state() private playing = false;
  @state() private progress = 0;
  @state() private mode: PlaybackMode = "studio";
  @state() private sandboxKitLoading = false;
  @state() private drummerStyle = "";
  @state() private tempo: Tempo = "1";
  @state() private hits = new Map<string, number>();
  @state() private sandboxStep = -1;
  @state() private enabledChannels = enabledStemChannels();
  @state() private channelLevels = defaultStemLevels();
  @state() private enabledFamilies: Record<DrumRow, boolean> = enabledDrumRows();
  @state() private familyLevels: Record<DrumRow, number> = defaultFamilyLevels();
  private audio?: StudioAudio;
  private sandboxTimer?: number;
  protected createRenderRoot() { return this; }

  connectedCallback(): void {
    super.connectedCallback();
    const params = new URLSearchParams(location.search);
    const requestedTempo = params.get("tempo");
    this.tempo = requestedTempo === "0.5" || requestedTempo === "1.2" ? requestedTempo : "1";
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this.destroyAudio();
  }

  willUpdate(changed: Map<PropertyKey, unknown>): void {
    if ((changed.has("slug") || changed.has("content")) && this.content) {
      this.destroyAudio();
      this.mode = "studio";
      this.enabledChannels = enabledStemChannels();
      this.channelLevels = defaultStemLevels();
      this.enabledFamilies = enabledDrumRows();
      this.familyLevels = defaultFamilyLevels();
      const study = this.study;
      const params = new URLSearchParams(location.search);
      const requested = params.get("drummer");
      this.drummerStyle = requested && [study?.drummer, ...(study?.alternates ?? [])].includes(requested) ? requested : study?.drummer ?? "";
      if (study?.audio && !this.manifestFor(study, this.tempo)) this.tempo = "1";
      if (study?.audio) void this.loadPlayable(study);
    }
  }

  private get study(): Study | undefined {
    return this.content?.catalog.studies.find((candidate) => candidate.slug === this.slug);
  }

  private async loadPlayable(study: Study): Promise<void> {
    this.loading = true;
    this.error = "";
    try {
      const lessonResponse = await fetch(study.audio!.lesson);
      if (!lessonResponse.ok) throw new Error(`Arrangement map returned ${lessonResponse.status}`);
      this.lesson = await lessonResponse.json() as LessonData;
      this.audio = new StudioAudio();
      this.audio.onProgress = (progress) => { this.progress = progress; };
      const manifest = this.manifestFor(study, this.tempo);
      if (!manifest) throw new Error(`No ${this.tempo}× studio render is available`);
      await this.audio.load(manifest, study.audio!.styles, this.drummerStyle);
      this.resetSandbox();
      this.syncAudioChannels();
    } catch (error) {
      this.error = error instanceof Error ? error.message : "The studio masters could not be loaded.";
    } finally {
      this.loading = false;
    }
  }

  private destroyAudio(): void {
    this.stopSandbox();
    this.audio?.destroy();
    this.audio = undefined;
    this.lesson = undefined;
    this.playing = false;
  }

  private notesForStyle(profile = this.drummerStyle): MidiNote[] {
    const styleSlug = this.study?.audio?.styles[profile];
    return styleSlug && this.lesson ? this.lesson.styles[styleSlug]?.notes ?? [] : [];
  }

  private manifestFor(study: Study, tempo: Tempo): string | undefined {
    return study.audio?.manifests?.[tempo] ?? (tempo === "1" ? study.audio?.manifest : undefined);
  }

  private stepsPerBeat(): number { return this.lesson?.stepsPerBeat ?? 4; }
  private beatsPerBar(): number { return this.lesson?.beatsPerBar ?? 4; }
  private stepTicks(): number { return this.lesson ? this.lesson.ppq / this.stepsPerBeat() : 0; }
  private stepCount(): number { return this.lesson ? Math.round(this.lesson.endTick / this.stepTicks()) : 0; }

  private originalSection(): string | undefined {
    const start = this.lesson?.excerpt?.referenceStartSeconds;
    if (start === undefined || !this.lesson) return undefined;
    const duration = this.lesson.endTick * 60 / (this.lesson.bpm * this.lesson.ppq);
    return `${timestamp(start)}–${timestamp(start + duration)}`;
  }

  private resetSandbox(): void {
    if (!this.lesson) return;
    const stepTicks = this.stepTicks();
    const next = new Map<string, number>();
    for (const note of this.notesForStyle()) {
      const key = `${rowForDrumNote(note)}:${Math.round(note.start / stepTicks)}`;
      next.set(key, Math.max(next.get(key) ?? 0, velocityLevel(note.velocity)));
    }
    this.hits = next;
  }

  private selectStyle(profile: string): void {
    this.drummerStyle = profile;
    this.audio?.selectStyle(profile);
    this.resetSandbox();
    this.updateUrl();
  }

  private updateUrl(): void {
    const url = new URL(location.href);
    url.searchParams.set("drummer", this.drummerStyle);
    url.searchParams.set("tempo", this.tempo);
    history.replaceState({}, "", url);
  }

  private async togglePlay(): Promise<void> {
    if (!this.audio) return;
    if (this.playing) {
      this.audio.stop();
      this.stopSandbox();
      this.playing = false;
      return;
    }
    if (this.mode === "sandbox" && !(await this.ensureSandboxKit())) return;
    await this.audio.play();
    this.playing = true;
    if (this.mode === "sandbox") this.startSandbox();
  }

  private syncAudioChannels(): void {
    for (const channel of ["guitar", "bass", "drums"] as StemChannel[]) {
      this.audio?.setChannelLevel(channel, this.channelLevels[channel]);
    }
    this.audio?.setChannel("guitar", this.enabledChannels.guitar);
    this.audio?.setChannel("bass", this.enabledChannels.bass);
    this.audio?.setChannel(
      "drums",
      this.mode === "studio" && this.enabledChannels.drums,
    );
    this.audio?.setSandboxDrums(
      this.mode === "sandbox" && this.enabledChannels.drums,
    );
    this.audio?.setSandboxDrumLevel(this.channelLevels.drums);
    for (const row of drumRows) {
      this.audio?.setSandboxVoiceLevel(row.id, this.familyLevels[row.id]);
    }
  }

  private setStemChannel(channel: StemChannel, enabled: boolean): void {
    this.enabledChannels = { ...this.enabledChannels, [channel]: enabled };
    this.syncAudioChannels();
  }

  private setStemLevel(channel: StemChannel, levelDb: number): void {
    this.channelLevels = { ...this.channelLevels, [channel]: levelDb };
    this.audio?.setChannelLevel(channel, levelDb);
    if (channel === "drums") this.audio?.setSandboxDrumLevel(levelDb);
  }

  private async ensureSandboxKit(): Promise<boolean> {
    if (!this.audio) return false;
    this.sandboxKitLoading = true;
    try {
      await this.audio.loadSandboxKit();
      return true;
    } catch (error) {
      this.error = error instanceof Error
        ? error.message
        : "The acoustic Sandbox kit could not be loaded.";
      return false;
    } finally {
      this.sandboxKitLoading = false;
    }
  }

  private async setMode(mode: PlaybackMode): Promise<void> {
    if (mode === this.mode) return;
    this.mode = mode;
    this.syncAudioChannels();
    if (mode === "studio") this.stopSandbox();
    else if (await this.ensureSandboxKit()) {
      if (this.mode === "sandbox" && this.playing) this.startSandbox();
    }
  }

  private startSandbox(): void {
    if (!this.lesson || this.sandboxTimer) return;
    this.audio?.setChannel("drums", false);
    const startDelay = this.audio?.millisecondsUntilStart() ?? 0;
    if (startDelay > 1) {
      this.sandboxTimer = window.setTimeout(() => {
        this.sandboxTimer = undefined;
        if (this.mode === "sandbox" && this.playing) this.startSandbox();
      }, startDelay);
      return;
    }
    const duration = (60_000 / (this.lesson.bpm * Number(this.tempo))) / this.stepsPerBeat();
    const count = this.stepCount();
    const exactStep = this.progress * count;
    const playStep = (step: number) => {
      this.sandboxStep = step;
      for (const row of drumRows) {
        const level = this.hits.get(`${row.id}:${this.sandboxStep}`) ?? 0;
        if (this.enabledChannels.drums && this.enabledFamilies[row.id] && level > 0) {
          this.audio?.playDrum(row.id, level);
        }
      }
    };
    const scheduleNext = (delay: number) => {
      this.sandboxTimer = window.setTimeout(() => {
        playStep((this.sandboxStep + 1) % count);
        scheduleNext(duration);
      }, delay);
    };
    playStep(Math.floor(exactStep) % count);
    const fractionThroughStep = exactStep - Math.floor(exactStep);
    scheduleNext(Math.max(4, duration * (1 - fractionThroughStep)));
  }

  private stopSandbox(): void {
    if (this.sandboxTimer) clearTimeout(this.sandboxTimer);
    this.sandboxTimer = undefined;
    this.sandboxStep = -1;
  }

  private toggleHit(row: DrumRow, step: number): void {
    if (this.mode !== "sandbox") return;
    const key = `${row}:${step}`;
    const next = new Map(this.hits);
    const level = next.get(key) ?? 0;
    if (level === 0) next.set(key, 0.35);
    else if (level < 0.5) next.set(key, 0.68);
    else if (level < 0.9) next.set(key, 1);
    else next.delete(key);
    this.hits = next;
  }

  private toggleFamily(row: DrumRow, enabled = !this.enabledFamilies[row]): void {
    if (this.mode !== "sandbox") return;
    this.enabledFamilies = { ...this.enabledFamilies, [row]: enabled };
  }

  private setFamilyLevel(row: DrumRow, levelDb: number): void {
    this.familyLevels = { ...this.familyLevels, [row]: levelDb };
    this.audio?.setSandboxVoiceLevel(row, levelDb);
  }

  private renderLevelControl(
    kind: "channel" | "family",
    id: StemChannel | DrumRow,
    label: string,
    levelDb: number,
  ) {
    const setLevel = (value: number) => {
      if (kind === "channel") this.setStemLevel(id as StemChannel, value);
      else this.setFamilyLevel(id as DrumRow, value);
    };
    return html`
      <div class="level-control">
        <input
          type="range"
          min="-12"
          max="6"
          step="1"
          .value=${String(levelDb)}
          aria-label="${label} volume"
          data-channel-level=${ifDefined(kind === "channel" ? id : undefined)}
          data-family-level=${ifDefined(kind === "family" ? id : undefined)}
          @input=${(event: Event) => setLevel(Number((event.target as HTMLInputElement).value))}
        >
        <output>${levelDb > 0 ? "+" : ""}${levelDb} dB</output>
        <button
          type="button"
          title="Reset ${label} to 0 dB"
          aria-label="Reset ${label} volume"
          @click=${() => setLevel(0)}
        >0</button>
      </div>
    `;
  }

  private renderChannelRow(channel: StemChannel) {
    const label = channel[0].toUpperCase() + channel.slice(1);
    const description = channel === "guitar"
      ? "Guitar track"
      : channel === "bass"
        ? "Bass track"
        : this.mode === "studio"
          ? "Complete drum performance"
          : "Entire live Sandbox kit";
    return html`
      <div class="mixer-row">
        <label>
          <input data-channel=${channel} type="checkbox" .checked=${this.enabledChannels[channel]} @change=${(event: Event) => this.setStemChannel(channel, (event.target as HTMLInputElement).checked)}>
          <i></i><span><b>${label}</b><small>${description}</small></span>
        </label>
        ${this.renderLevelControl("channel", channel, label, this.channelLevels[channel])}
      </div>
    `;
  }

  private renderFamilyRow(row: typeof drumRows[number]) {
    return html`
      <div class="mixer-row piece-row">
        <label>
          <input data-drum-family=${row.id} type="checkbox" .checked=${this.enabledFamilies[row.id]} @change=${(event: Event) => this.toggleFamily(row.id, (event.target as HTMLInputElement).checked)}>
          <i></i><span><b>${row.label}</b><small>Individual acoustic drum</small></span>
        </label>
        ${this.renderLevelControl("family", row.id, row.label, this.familyLevels[row.id])}
      </div>
    `;
  }

  private setTempo(tempo: Tempo): void {
    if (tempo === this.tempo) return;
    if (this.study && !this.manifestFor(this.study, tempo)) return;
    const wasPlaying = this.playing;
    this.audio?.destroy();
    this.audio = undefined;
    this.stopSandbox();
    this.playing = false;
    this.tempo = tempo;
    this.updateUrl();
    if (this.study?.audio) void this.loadPlayable(this.study).then(() => { if (wasPlaying) void this.togglePlay(); });
  }

  private rowEvents(kind: "guitar" | "bass" | DrumRow): MidiNote[] {
    if (!this.lesson) return [];
    if (kind === "guitar" || kind === "bass") return this.lesson.instruments[kind];
    return this.notesForStyle().filter((note) => rowForDrumNote(note) === kind);
  }

  private renderSequencer() {
    if (!this.lesson) return "";
    const rows: Array<[string, "guitar" | "bass" | DrumRow]> = [
      ["Guitar", "guitar"],
      ["Bass", "bass"],
      ...drumRows.map((row) => [row.label, row.id] as [string, DrumRow]),
    ];
    const stepTicks = this.stepTicks();
    const count = this.stepCount();
    const stepsPerBar = this.stepsPerBeat() * this.beatsPerBar();
    return html`<div class="study-seq" style="--steps:${count}">
      <span class="seq-ruler-title">${this.lesson.bars ?? Math.round(count / stepsPerBar)} bars</span>
      <div class="seq-ruler">${Array.from({ length: count }, (_, step) => {
        const barStart = step % stepsPerBar === 0;
        const beatStart = step % this.stepsPerBeat() === 0;
        const bar = Math.floor(step / stepsPerBar) + 1;
        const beat = Math.floor((step % stepsPerBar) / this.stepsPerBeat()) + 1;
        return html`<span class="seq-marker ${barStart ? "bar-start" : beatStart ? "beat-start" : ""}">${barStart ? `BAR ${bar} · 1` : beatStart ? beat : ""}</span>`;
      })}</div>
      ${rows.map(([label, kind]) => {
        const occupied = new Map<number, { level: number; offsets: number[] }>();
        for (const note of this.rowEvents(kind)) {
          const exactStep = note.start / stepTicks;
          const step = Math.round(exactStep);
          const offset = exactStep - step;
          const existing = occupied.get(step) ?? { level: 0, offsets: [] };
          existing.level = Math.max(existing.level, velocityLevel(note.velocity));
          if (!existing.offsets.some((candidate) => Math.abs(candidate - offset) < 0.001)) {
            existing.offsets.push(offset);
          }
          occupied.set(step, existing);
        }
        const drum = !["guitar", "bass"].includes(kind);
        const muted = drum && !this.enabledFamilies[kind as DrumRow];
        const editableDrum = drum && this.mode === "sandbox";
        return html`${editableDrum ? html`<button class="seq-label seq-row-toggle ${muted ? "muted" : ""}" @click=${() => this.toggleFamily(kind as DrumRow)}>${muted ? "Unmute" : "Mute"} ${label}</button>` : html`<span class="seq-label">${label}</span>`}<div class="seq-row">${Array.from({ length: count }, (_, step) => {
          const drum = !["guitar", "bass"].includes(kind);
          const sourceCell = occupied.get(step);
          const level = this.mode === "sandbox" && drum ? this.hits.get(`${kind}:${step}`) ?? 0 : sourceCell?.level ?? 0;
          const timingOffsets = this.mode === "sandbox" && drum
            ? (level > 0 ? [0] : [])
            : sourceCell?.offsets ?? [];
          const current = this.mode === "sandbox" ? this.sandboxStep === step : Math.floor(this.progress * count) === step;
          const barStart = step % stepsPerBar === 0;
          const beatStart = step % this.stepsPerBeat() === 0;
          const strength = level === 0 ? "off" : level < 0.5 ? "soft" : level < 0.9 ? "normal" : "strong accent";
          return html`<button data-row=${kind} class="seq-cell ${strengthClass(level)} ${barStart ? "bar-start" : beatStart ? "beat-start" : ""} ${current ? "current" : ""}" ?disabled=${!editableDrum} @click=${() => editableDrum && this.toggleHit(kind as DrumRow, step)} aria-label="${label}, step ${step + 1}, ${strength}">${timingOffsets.map((offset) => html`<i class="seq-timing" style=${`--timing-position:${(offset + 0.5) * 100}%`}></i>`)}</button>`;
        })}</div>`;
      })}
    </div><div class="seq-legend"><span><i class="soft"></i>soft / ghost</span><span><i class="normal"></i>normal</span><span><i class="strong"></i>strong accent · white rim</span><span><i class="timing"></i>exact timing inside cell</span></div><p class="seq-hint">${this.lesson.bars ?? 4} bars, ${this.beatsPerBar()} beats each. Gray rules mark beats; bright rules start each bar. A thin white mark shows the strike's exact position inside a cell; two marks mean two closely spaced strikes. In Sandbox, click a row name to mute it or click a drum cell repeatedly to cycle soft → normal → strong → off.</p>`;
  }

  render() {
    const study = this.study;
    if (!study) return html`<main class="empty-state"><h1>Study not found</h1><a href="/graph">Return to graph</a></main>`;
    const profiles = profileMap(this.content);
    const techniques = techniqueMap(this.content);
    const original = profiles.get(study.drummer)!;
    const styles = [study.drummer, ...study.alternates];
    const originalSection = this.originalSection();
    const referenceUrl = this.lesson?.excerpt?.referenceUrl;
    if (!study.audio) return html`
      <main class="research-study">
        <a class="back-link" href="/graph">← Evolution graph</a>
        <header class="research-hero">
          <div class="eyebrow">${study.year} · Coming later</div>
          <h1>${study.band}</h1><h2>${study.track}</h2>
          <p class="lede">${study.focus}</p>
          <div class="tag-row">${study.techniques.map((slug) => html`<span style="--tech:${techniques.get(slug)?.color}">${techniques.get(slug)?.label}</span>`)}</div>
        </header>
        <section class="research-grid">
          <article class="panel"><span class="eyebrow">Original performance</span><h3>${original.name}</h3><p>${original.band}</p><ul>${original.traits.map((trait) => html`<li>${trait}</li>`)}</ul></article>
          <article class="panel"><span class="eyebrow">Three contrasting approaches</span>${study.alternates.map((slug, index) => { const profile = profiles.get(slug)!; return html`<div class="contrast-row"><span>0${index + 1}</span><p><b>${profile.name}</b><small>${profile.band} · ${profile.traits.join(" · ")}</small></p></div>`; })}</article>
          <article class="panel queue-note"><span class="eyebrow">Interactive study</span><h3>Listening example coming later.</h3><p>You can explore this performance and its rhythmic connections now. Its playable four-bar drummer comparison will be added later.</p></article>
        </section>
      </main>`;

    return html`
      <main class="study-page">
        <a class="back-link" href="/graph">← Evolution graph</a>
        <header class="study-hero">
          <div><div class="eyebrow">${study.band} / ${study.album}, ${study.year}</div><h1>${study.track}</h1><p>${study.focus}</p></div>
          <div class="study-facts"><span><b>${study.audio.bars}</b> bars</span><span><b>${study.audio.meter}</b> meter</span><span><b>${(study.audio.bpm * Number(this.tempo)).toFixed(this.tempo === "1" ? 1 : 2)}</b> BPM</span>${originalSection ? html`<span>${referenceUrl ? html`<a class="reference-time" href=${referenceUrl} target="_blank" rel="noopener noreferrer" aria-label="Open the original recording at ${originalSection}"><b>${originalSection}</b></a>` : html`<b>${originalSection}</b>`} original section</span>` : ""}</div>
        </header>
        ${this.error ? html`<div class="error-banner">${this.error}</div>` : ""}
        <section class="transport panel">
          <button class="play-button" ?disabled=${this.loading || !!this.error} @click=${this.togglePlay}><span class="play-symbol">${this.playing ? "■" : "▶"}</span><span><small>${this.loading ? "Loading studio audio" : this.sandboxKitLoading ? "Loading acoustic kit" : this.mode === "studio" ? "Original-tempo studio" : "Editable acoustic kit"}</small><b>${this.playing ? "Stop loop" : "Play loop"}</b></span></button>
          <div class="mode-switch"><span>Playback</span><button class=${this.mode === "studio" ? "active" : ""} @click=${() => void this.setMode("studio")}>Studio</button><button class=${this.mode === "sandbox" ? "active" : ""} @click=${() => void this.setMode("sandbox")}>Sandbox</button></div>
          <div class="tempo-switch"><span>Tempo</span>${[["0.5", "½×"], ["1", "1×"], ["1.2", "1.2×"]].map(([value, label]) => html`<button class=${this.tempo === value ? "active" : ""} ?disabled=${!this.manifestFor(study, value as Tempo)} title=${value === "1" ? "Original tempo" : this.manifestFor(study, value as Tempo) ? "Alternative listening tempo" : "Alternative listening tempo coming later"} @click=${() => this.setTempo(value as Tempo)}>${label}</button>`)}</div>
          <div class="progress"><i style="width:${this.progress * 100}%"></i></div>
        </section>

        <section class="study-workspace">
          <div class="drummer-column">
            <div class="section-heading compact"><div><span class="section-number">01</span><h2>Choose the drummer</h2></div></div>
            <div class="drummer-list">${styles.map((slug, index) => { const profile = profiles.get(slug)!; return html`<button class=${this.drummerStyle === slug ? "active" : ""} @click=${() => this.selectStyle(slug)}><i></i><span><b>${index === 0 ? "Original" : `In the style of ${profile.name}`}</b><small>${profile.name} · ${profile.band}</small><em>${profile.traits.join(" · ")}</em></span></button>`; })}</div>
          </div>
          <aside class="mixer panel">
            <div class="section-heading compact"><div><span class="section-number">02</span><h2>Mix the parts</h2></div></div>
            ${(["guitar", "bass", "drums"] as StemChannel[]).map((channel) => this.renderChannelRow(channel))}
            ${this.mode === "studio"
              ? html`<p class="mixer-mode-note">Balance guitar, bass, and the complete drum performance. Switch to Sandbox to change individual kit pieces.</p>`
              : html`<p class="mixer-mode-note">${this.sandboxKitLoading ? "Loading the acoustic kit…" : this.enabledChannels.drums ? "Individual acoustic kit pieces" : "Entire kit muted — piece choices and levels are preserved"}</p>${drumRows.map((row) => this.renderFamilyRow(row))}`
            }
          </aside>
        </section>
        <section class="sequencer-panel panel"><div class="section-heading"><div><span class="section-number">03</span><h2>${this.mode === "studio" ? "Read the arrangement" : "Rewrite the drum part"}</h2></div><p>${this.mode === "studio" ? "Loudness is shown as soft, normal, or a white-rimmed strong accent." : "Click any drum cell. Edits disappear when you leave."}</p></div>${this.lesson?.excerpt ? html`<div class="excerpt-note"><b>${this.lesson.excerpt.label}</b><span>${this.lesson.excerpt.sourceBars ? `transcribed bars ${this.lesson.excerpt.sourceBars[0]}–${this.lesson.excerpt.sourceBars[1]} · ` : ""}${originalSection ? html`original recording ${referenceUrl ? html`<a class="reference-time" href=${referenceUrl} target="_blank" rel="noopener noreferrer" aria-label="Open the original recording at ${originalSection}">${originalSection}</a>` : originalSection} · ` : ""}true tempo</span></div>` : ""}${this.renderSequencer()}</section>
        <section class="listening-note"><span></span><div><div class="eyebrow">Listening prompt</div><p>${this.lesson && this.study?.audio ? this.lesson.styles[this.study.audio.styles[this.drummerStyle]]?.description : study.focus}</p></div></section>
      </main>`;
  }
}
